/**
 * NOUVEAUTES — ce qui a change pour les utilisateurs, version par version
 * (2026-10-02). C'est la source de l'ecran Nouveautes, ouvert depuis le
 * menu utilisateur.
 *
 * Trois regles d'ecriture :
 *   - deux ou trois phrases par changement, pour qui se sert de l'ecran,
 *     sans nom de table ni de colonne — le registre technique est celui
 *     de CHANGELOG.md, qui reste la reference de ce qui a ete fait ;
 *   - un changement porte la PERMISSION qui le rend visible : la page ne
 *     montre a chacun que ce qu'il peut rencontrer. Sans permission, il
 *     s'adresse a tous ;
 *   - la version de tete porte `date: null` tant qu'elle n'est pas
 *     deployee : on y ajoute au fil des lots, et la date se pose au
 *     deploiement — `deploy.ps1` refuse de partir sans elle. Les dates
 *     sont celles des DEPLOIEMENTS, pas celles des commits.
 */

export interface Changement {
  /** Rubrique : l'ecran ou la notion, pour regrouper a l'affichage. */
  ecran: string;
  titre: string;
  texte: string;
  /** Visible a qui detient l'une de ces permissions. Absent : visible de tous. */
  permission?: string | string[];
}

export interface VersionNouveautes {
  version: string;
  /** Date du deploiement, ISO. `null` : pas encore deployee. */
  date: string | null;
  changements: Changement[];
}

const CONTRATS = 'contrat.lire';
const ADHERENTS = 'personne.lire';
const INSCRIPTIONS = 'inscription.lire';
const PAIEMENTS = 'reglement.lire';
const REMISES = 'remise.lire';
const PLANNING = 'creneau.lire';
const ACTIVITES = 'activite.lire';
const ENTITES = 'entite.lire';
const DEMANDES = 'demande.lire';
const UTILISATEURS = 'utilisateur.lire';
const MESSAGES = 'message.envoyer';
const BROUILLONS = 'message.brouillon';

export const NOUVEAUTES: VersionNouveautes[] = [
  {
    version: '0.75.0',
    date: '2026-11-04',
    changements: [
      {
        ecran: 'Équipe',
        titre: 'Les documents du Drive s’affichent plus vite',
        texte:
          'Le bloc Documents de la fiche salarié s’affiche environ trois fois plus vite, et ne lit plus le Drive tant qu’il est replié. La fenêtre d’envoi des fiches de paie s’ouvre aussitôt : la colonne Fiche se remplit quelques secondes plus tard, et l’envoi devient possible à ce moment-là.',
        permission: CONTRATS,
      },
      {
        ecran: 'Équipe',
        titre: 'Envoyer leur fiche de paie aux salariés',
        texte:
          'Dans la synthèse d’un mois clôturé, l’enveloppe en bout de ligne envoie sa fiche à un salarié, et « Envoyer les fiches de paie » l’envoie à tous. La fiche est prise sur le Drive et part par courriel du compte de l’école, les réponses arrivant sur l’adresse de l’école. La fenêtre montre pour chacun l’adresse, le fichier trouvé et ce qui a déjà été envoyé ; une fiche déjà partie ne repart qu’après confirmation.',
        permission: 'paie.envoyer_fiche',
      },
      {
        ecran: 'Équipe',
        titre: 'Le justificatif d’un titre de transport est fourni ou absent',
        texte:
          'Dans le bloc Titres de transport de la fiche contrat, le champ libre laisse place à une case Justificatif : Fourni ou Absent, à cocher directement dans le tableau, même sur une période verrouillée. L’infobulle garde la date à laquelle le justificatif a été remis. Il se fournit chaque année scolaire : au 1er septembre, toutes les cases repassent à « absent » et le salarié doit présenter à nouveau son titre.',
        permission: CONTRATS,
      },
      {
        ecran: 'Équipe',
        titre: 'Le transport est remboursé à 50 % au-delà d’un mi-temps, à 25 % sinon',
        texte:
          'La part du titre de transport remboursée dépend désormais du temps de travail du mois : la moitié du titre au-delà d’un mi-temps, le quart sinon. Le mi-temps se mesure sur la base du temps plein du statut (24 h pour un professeur, 26 h pour un animateur, 35 h pour un administratif), avec les heures de la paie du mois. Les mois déjà clôturés gardent ce qui a été déclaré. Le montant saisi sur une période est le prix du titre, et le forfait Imagine R rejoint le pass Navigo et les titres unitaires ; les montants repris du classeur ont été ramenés au prix du Navigo. La colonne Remboursé du bloc Titres de transport disparaît : la part se lit dans l’élément de paie du mois, avec les heures qui la fondent.',
        permission: CONTRATS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Changer de créneau en cours d’année, sans changer le montant dû',
        texte:
          'Quand un élève change d’horaire dans la même activité après avoir suivi des cours, « Transférer » propose un changement de créneau à une date : on choisit le dernier cours sur l’ancien créneau et le premier sur le nouveau (proposé : le cours de la semaine suivante). Le montant dû ne change pas, il se partage entre les deux inscriptions au prorata des séances faites, et les règlements déjà ventilés suivent. La fiche montre alors deux lignes pour la même activité, l’ancienne en fin anticipée. Le simple déplacement d’horaire reste possible pour un élève qui n’a pas encore commencé.',
        permission: 'inscription.transferer',
      },
      {
        ecran: 'Adhérents',
        titre: 'L’attestation de paiement dit le montant dû, ce qui a été reçu et le reste à payer',
        texte:
          'Chaque inscription porte désormais son montant, avec sa date de fin ou de démarrage en cours d’année quand il y en a une, puis le montant dû pour l’année. Le détail des règlements comprend les chèques remis pas encore encaissés, sans colonne de date, et une ligne Reste à payer suit le total quand l’année n’est pas soldée. L’attestation est donc de nouveau disponible même quand l’année n’est pas soldée, dès qu’un règlement a été reçu. Elle ne l’est pas tant qu’un forfait d’essai n’est ni confirmé ni arrêté.',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Partout',
        titre: 'Les infobulles ont toutes le même aspect',
        texte:
          'Toute explication qui apparaît au survol — une note d’adhérent, un calcul de la paie, le sens d’une icône, le nom d’une école — s’affiche désormais dans la même bulle sombre, tout de suite, au-dessus de l’élément survolé. Fini les petites bulles grises du navigateur qui mettaient une seconde à venir.',
      },
      {
        ecran: 'Équipe',
        titre: 'La synthèse mensuelle signale un transport sans justificatif',
        texte:
          'Dans la synthèse mensuelle de la paie, un montant de transport souligné en pointillé dit que le justificatif du titre n’a pas été fourni pour l’année scolaire ; l’infobulle le rappelle, et l’élément de paie du mois aussi.',
        permission: CONTRATS,
      },
      {
        ecran: 'Équipe',
        titre: 'Le bloc Contrats de la fiche salarié nomme le titre de transport',
        texte:
          'La colonne Transport dit le type du titre — Pass Navigo, titres unitaires, forfait Imagine R — plutôt que son prix, qui reste dans l’infobulle de l’icône. Le titre du bloc perd sa mention « à date ».',
        permission: CONTRATS,
      },
    ],
  },
  {
    version: '0.74.0',
    date: '2026-11-03',
    changements: [
      {
        ecran: 'Adhérents',
        titre: 'L’attestation de paiement suit une nouvelle trame',
        texte:
          'La phrase d’attestation nomme l’adhérent et liste ses inscriptions avec le jour et l’horaire de chaque cours, sans montant par ligne. Le pied de page porte désormais l’identité de l’école : « École de Musique » suivi de son nom, adresse, site, email, téléphone, agrément Jeunesse Education Populaire et SIRET, tels qu’ils sont saisis sur la fiche entité. La ligne de total du tableau n’est plus coupée par les séparateurs de colonnes. Et le bouton Attestation n’est actif qu’une fois l’année soldée : les règlements encaissés doivent couvrir ce que l’adhérent doit, cotisation comprise — un chèque prévu ne compte pas tant qu’il n’est pas encaissé. Sinon, l’infobulle du bouton dit ce qui reste à encaisser.',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Les prorata s’arrondissent à l’euro inférieur',
        texte:
          'Le montant proposé pour un démarrage en cours d’année, une fin anticipée ou un transfert tombe désormais sur un euro rond, en dessous : 31 € pour une séance sur trente à 950 €, et non 31,67 €. Les montants déjà enregistrés ne changent pas.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Équipe',
        titre: 'Les titres de transport se saisissent par période',
        texte:
          'La fiche contrat remplace le montant unique du titre de transport par un bloc Titres de transport, sous la mutuelle : un pass Navigo ou des titres unitaires, du … au …, pour un montant mensuel et un justificatif. L’école en rembourse la moitié sur chaque mois du contrat que la période touche. Une période sans fin vaut pour les mois à venir, en ajouter une ferme la précédente à la veille, et une période qui a servi à un mois de paie clôturé ne se modifie plus. Les titres déjà saisis sont devenus des périodes Navigo depuis le début du contrat. Le détail de paie nomme le titre du mois affiché.',
        permission: CONTRATS,
      },
      {
        ecran: 'Administration',
        titre: 'L’agrément JEP sur la fiche entité',
        texte:
          'Le bloc Identité de la fiche entité gagne un champ Agrément JEP, sur la ligne du n° GUSO. Il est imprimé au pied de l’attestation de paiement, à côté du SIRET ; vide, la mention est omise. Les coordonnées du pied (site, email, téléphone) viennent du bloc Adresse et coordonnées.',
        permission: ENTITES,
      },
    ],
  },
  {
    version: '0.73.0',
    date: '2026-11-02',
    changements: [
      {
        ecran: 'Adhérents',
        titre: 'Un bloc Commentaires sur la fiche',
        texte:
          'La fiche adhérent gagne un bloc Commentaires entre Paiements et Adresses. On y écrit un texte libre avec une mise en forme légère : gras, italique, souligné, alignement, paragraphes. Les notes déjà présentes — contributions à la vie de la chorale saisies en ligne — y apparaissent ; la ligne « Parents (repris du classeur) » que l’import avait laissée a été retirée partout où les parents sont nommés dans les contacts. L’export Excel des adhérents porte une colonne Commentaire, sans la mise en forme.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Un bloc-notes devant le nom quand la fiche porte un commentaire',
        texte:
          'Dans la liste des adhérents, un petit bloc-notes précède le nom de ceux dont la fiche porte un commentaire. Le texte de la note s’affiche en infobulle au passage de la souris, sans ouvrir la fiche.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Une attestation de paiement en PDF depuis la fiche',
        texte:
          'Dans le bloc Paiements de la fiche, un bouton Attestation, à gauche de Rembourser, produit l’attestation de paiement de l’adhérent pour l’année affichée : les règlements encaissés, au titre de ses inscriptions et de sa cotisation, sur papier à en-tête de l’école. On choisit à qui l’envoyer parmi l’adresse de la fiche et celles des contacts, « Voir le PDF » le montre avant, puis un message Gmail s’ouvre, prêt à partir. Rien n’est conservé.',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Adhérents',
        titre: 'L’attestation porte le logo de l’école',
        texte:
          'L’en-tête de l’attestation de paiement affiche le logo de l’école, à gauche de son identité : celui chargé sur la fiche de l’entité (Administration → Entités), à défaut le logo Artistic, comme le bandeau de l’application.',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Connexion',
        titre: 'L’attestation part en pièce jointe, depuis votre boîte Gmail',
        texte:
          'À votre prochaine connexion avec « Se connecter avec Google », une fenêtre vous demandera, une seule fois, l’autorisation de composer des messages dans votre boîte. Dès lors, l’attestation de paiement se pose dans Gmail en brouillon, PDF en pièce jointe, prêt à relire et envoyer. Cette demande ne concerne que les rôles qui disposent de la permission « Envoi d’email » (Direction et Gestionnaire) ; « Mes informations » montre l’état de l’accès et permet de le retirer. Sans accès, l’attestation est enregistrée sur le poste et le message s’ouvre sans le fichier, qu’il reste à glisser. L’application ne lit jamais votre boîte.',
        permission: BROUILLONS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Retirer le seul élève d’un créneau démarré après la clôture passe à nouveau',
        texte:
          'Supprimer ou transférer l’unique inscription d’un créneau qui a commencé dans un mois de paie encore ouvert était refusé au nom d’un mois qui n’était pas clôturé. Le contrôle ne regarde plus que les mois réellement déclarés : ce qui ne touche que des mois ouverts passe.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Contrats',
        titre: 'Éligible à la base forfaitaire',
        texte:
          'La fiche d’un contrat de professeur ou d’animateur porte une case « Éligible à la base forfaitaire », dans le bloc Statut et rémunération. Elle est cochée pour tous les contrats existants ; décochez-la pour ceux qui ne relèvent pas de l’assiette forfaitaire. Un administratif n’y est jamais éligible : la case ne s’affiche pas pour lui. Sans effet sur le calcul de la paie.',
        permission: CONTRATS,
      },
      {
        ecran: 'Paie',
        titre: 'La synthèse mensuelle dit la base forfaitaire, plus l’ETP',
        texte:
          'Dans la synthèse d’un mois, la colonne ETP laisse place à « Base forfaitaire » : Oui ou Non pour chaque salarié, selon la case de son contrat. L’export CSV suit.',
        permission: CONTRATS,
      },
      {
        ecran: 'Paie',
        titre: 'Les durées de la synthèse s’arrondissent aux cinq minutes, vers le bas',
        texte:
          'La colonne Durée de la synthèse mensuelle s’arrondit aux cinq minutes inférieures, et non plus au quart d’heure le plus proche : un temps plein à 35 heures s’affiche 151 h 40, la durée légale, au lieu de 151 h 45. Aucune durée affichée ne dépasse plus sa valeur réelle. La valeur exacte reste lisible en survolant la durée ; l’export CSV suit.',
        permission: CONTRATS,
      },
    ],
  },
  {
    version: '0.72.0',
    date: '2026-10-31',
    changements: [
      {
        ecran: 'Équipe',
        titre: 'L’équivalent temps plein sur la synthèse du mois',
        texte:
          'Dans Paie, la synthèse d’une période gagne une colonne ETP après la durée : la durée du mois rapportée à la base du temps plein du statut (24 h professeur, 26 h animateur, 35 h administratif). Le pied du tableau en donne la somme, et l’export Excel la reprend. C’est un calcul d’affichage, rien n’est enregistré.',
        permission: CONTRATS,
      },
    ],
  },
  {
    version: '0.71.0',
    date: '2026-10-30',
    changements: [
      {
        ecran: 'Équipe',
        titre: 'La prime d’ancienneté se calcule par mois, en années complètes',
        texte:
          'Les points d’ancienneté ne sont plus figés au 1er septembre : chaque mois de paie prend les points acquis ce mois-là, 2 par année complète dès 24 mois, en entiers. Ils changent au mois anniversaire de l’entrée dans l’école. Un mois déjà clôturé garde la prime déclarée.',
        permission: CONTRATS,
      },
      {
        ecran: 'Équipe',
        titre: 'L’ancienneté se lit dans le titre du bloc Contrats',
        texte:
          'Sur la fiche d’un salarié, le bloc Contrats annonce les mois d’ancienneté depuis le premier contrat dans l’école et les points acquis à ce jour ; son tableau n’a plus de colonnes Ancienneté et Points, et gagne deux colonnes Mutuelle et Transport : date de souscription, montant du titre et part remboursée. Les écrans Contrats et Paie montrent les points du mois en cours, en entiers.',
        permission: CONTRATS,
      },
      {
        ecran: 'Équipe',
        titre: 'L’ancienneté et les points sur la liste du personnel',
        texte:
          'Dans Gestion de l’équipe pédagogique, chaque contrat en cours affiche, après le tag CDI ou CDD, l’ancienneté en mois et les points du mois en cours. Un salarié des deux écoles a ses deux contrats l’un sous l’autre.',
        permission: CONTRATS,
      },
      {
        ecran: 'Administration',
        titre: 'Le code NAF et le site web sur la fiche entité',
        texte:
          'Le bloc Identité de la fiche d’une entité porte le code NAF (APE) de l’employeur, à côté du SIRET : quatre chiffres et une lettre, le point de l’INSEE est accepté. Le n° GUSO passe sur la ligne de la couleur d’identification ; les explications du NAF, du GUSO, de la convention et de la couleur s’affichent au survol de leur libellé. Le code d’une entité existante ne se modifie plus. Le bloc Adresse et coordonnées gagne l’adresse du site web de l’école.',
        permission: ENTITES,
      },
      {
        ecran: 'Administration',
        titre: 'La fiche entité dit quel champ est refusé',
        texte:
          'Quand une saisie est refusée, la fiche affiche le motif par champ — « Code IDCC attendu sur 4 chiffres » — au lieu du seul « Les données transmises sont invalides ».',
        permission: ENTITES,
      },
    ],
  },
  {
    version: '0.70.0',
    date: '2026-10-30',
    changements: [
      {
        ecran: 'Équipe',
        titre: 'Clôturer une période de paie fige tout le mois',
        texte:
          'Jusqu’ici la clôture n’enregistrait que les lignes de cours : le salaire fixe, la prime d’ancienneté, le transport et la mutuelle restaient recalculés à chaque ouverture, même sur un mois déclaré. Désormais la clôture enregistre, pour chaque salarié, ce qui est déclaré — salaire, prime, heures, transport, mutuelle — et ne bouge plus, quoi que l’on change ensuite sur le contrat ou dans les variables de paie. Le détail d’un mois clos n’est plus conservé : il se recalcule depuis les inscriptions et le contrat, sous une ligne « Déclaré à la clôture » qui dit ce qui a été payé. Si une inscription corrigée après coup change ce détail, l’écart est régularisé sur le premier mois ouvert, en une ligne « Régularisation des mois clos ». Pour corriger un mois clos, on le rouvre.',
        permission: CONTRATS,
      },
    ],
  },
  {
    version: '0.69.0',
    date: '2026-10-27',
    changements: [
      {
        ecran: 'Paiements',
        titre: 'Relancer un impayé depuis le rapprochement',
        texte:
          'Chaque ligne du rapprochement qui porte un reste à payer gagne un lien Relancer. Il ouvre dans Gmail un courriel prêt à partir : aux parents pour un mineur, à l’adhérent pour un adulte, avec le reste dû, la cotisation si elle n’est pas soldée et les coordonnées bancaires de l’école quand elles sont saisies. Vous relisez, vous envoyez.',
        permission: MESSAGES,
      },
      {
        ecran: 'Administration',
        titre: 'Les coordonnées bancaires de l’école se saisissent sur sa fiche',
        texte:
          'La fiche de l’entité gagne un bloc Coordonnées bancaires, sous l’adresse : libellé, titulaire, IBAN et BIC de chaque compte. La clé de l’IBAN est vérifiée à la saisie, et le bordereau de remise en tire la banque, le guichet et le numéro de compte. Un compte qui a servi à une remise se désactive au lieu de se supprimer.',
        permission: ENTITES,
      },
      {
        ecran: 'Adhérents',
        titre: 'Créer un adhérent vérifie d’abord qu’il n’existe pas déjà',
        texte:
          'Si une fiche porte déjà le même nom et le même prénom, accents et majuscules mis à part, la création s’arrête et montre la ou les fiches trouvées, avec un lien vers chacune. On ouvre la fiche existante, ou l’on confirme « créer quand même » s’il s’agit bien d’une autre personne.',
        permission: 'personne.creer',
      },
      {
        ecran: 'Adhérents',
        titre: 'Un adhérent sans inscription active n’est plus membre usager',
        texte:
          'Quand toutes les inscriptions de l’année d’un adhérent sont arrêtées, par une fin anticipée ou une inscription inactive, il n’est plus compté comme membre usager, ni comme membre actif même s’il cotise. La fiche, le filtre Type de la liste, le rapprochement et les tableaux de bord suivent la même règle.',
        permission: 'personne.lire',
      },
      {
        ecran: 'Équipe',
        titre: 'La prime d’ancienneté vaut aussi pour les administratifs',
        texte:
          'Un salarié administratif touche sa prime d’ancienneté aux mêmes règles qu’un professeur : points × valeur du point, en entier à temps plein, au prorata de ses heures hebdomadaires à temps partiel. Elle s’ajoute à la ligne Salaire mensuel dans la paie, le tableau annuel et la synthèse.',
        permission: CONTRATS,
      },
    ],
  },
  {
    version: '0.68.0',
    date: '2026-10-17',
    changements: [
      {
        ecran: 'Équipe',
        titre: 'Un salaire fixe se saisit en net ou en brut',
        texte:
          'Dans le bloc Salaire mensuel d’un contrat, une liste déroulante dit si le montant saisi est un net ou un brut ; rien n’est converti. Sur l’écran Paie, tous les montants sont des nets, et le salaire d’un administratif saisi en brut est souligné, dans le tableau annuel, la synthèse et le détail ; une légende sous le tableau le rappelle.',
        permission: CONTRATS,
      },
      {
        ecran: 'Équipe',
        titre: 'L’ancienneté compte depuis le premier contrat dans l’école',
        texte:
          'Les CDD déclarés au CEA avant un CDI sont désormais des contrats à part sur la fiche du salarié, avec leur numéro CEA, leur motif et leurs heures en commentaire. L’ancienneté, et donc la prime, court depuis le premier de ces contrats dans l’école : découper une carrière en plusieurs contrats ne change rien à la paie.',
        permission: CONTRATS,
      },
    ],
  },
  {
    version: '0.67.0',
    date: '2026-10-13',
    changements: [
      {
        ecran: 'Adhérents',
        titre: 'Le droit à l’image se résume à un seul choix',
        texte:
          'Le bloc Droit à l’image de la fiche ne distingue plus quatre supports : un seul choix, non renseigné, accordé ou refusé, avec la date de signature et un commentaire. Les autorisations déjà saisies sont reprises — refusée si un support l’était, accordée sinon.',
        permission: 'personne.lire',
      },
      {
        ecran: 'Planning',
        titre: 'La liste de présence, tous les cours suivis sur un mois',
        texte:
          'Planning → Liste de présence montre, sur le mois affiché, tous les inscrits des cours dont le type d’activité suit les présences : une section par créneau pour les cours de groupe, une section par activité pour les cours individuels, chaque élève sur son propre jour. Les filtres Professeur, Jour et Activité se répondent. On y pointe comme sur la feuille d’un créneau, chaque clic enregistre. La liste s’ouvre sur le mois courant ; ‹ et › le changent.',
        permission: 'presence.lire',
      },
    ],
  },
  {
    version: '0.66.0',
    date: '2026-10-13',
    changements: [
      {
        ecran: 'Planning',
        titre: 'La feuille de présence d’un créneau',
        texte:
          'Sur les créneaux dont le type d’activité suit les présences, une icône de liste apparaît à gauche du crayon. Elle ouvre la feuille du créneau : une ligne par inscrit, une colonne par séance de l’année, un total d’absences par élève et un bilan par date. Un clic sur une case passe de non saisi à présent, puis absent, puis non saisi — chaque clic est enregistré aussitôt, et le journal garde qui a pointé quoi. « tous ✓ » sous une date remplit la colonne. Seules les séances passées ou du jour se saisissent ; les cases d’avant un démarrage en cours d’année ou d’après une fin anticipée sont grisées. Les inscriptions arrêtées restent sur la feuille.',
        permission: 'presence.lire',
      },
      {
        ecran: 'Référentiels',
        titre: 'Le suivi des présences se règle sur le type d’activité',
        texte:
          'La fiche d’un type d’activité porte un champ « Suivi des présences », sous la nature du cours : non applicable, présence (chaque élève est pointé) ou absence seulement (on ne note que les absents, une case vide vaut présent). Tous les types sont à « non applicable » pour commencer : c’est ce réglage qui fait apparaître la feuille de présence sur le planning.',
        permission: 'type_activite.lire',
      },
      {
        ecran: 'Référentiels',
        titre: 'Types de réduction',
        texte:
          'Les réductions ont désormais un nom : Référentiels → Types de réduction liste, pour chaque année, les réductions qu’une inscription peut porter, avec leur montant et un rappel du critère (« moins de 26 ans ou plus de 65 ans »). Un type « Remise libre » se chiffre et se motive à l’inscription. Chaque type s’applique à toutes les activités, ou seulement aux inscriptions de certains types d’activité — la réduction chorale ne se propose pas sur un piano. Le montant est figé sur chaque inscription à la saisie : le modifier ici ne change rien de déjà saisi. L’ordre de la liste est celui de la saisie d’inscription : glissez les lignes pour le changer. Six types sont proposés d’office, à renommer, limiter ou compléter.',
        permission: 'type_reduction.lire',
      },
      {
        ecran: 'Référentiels',
        titre: 'Les types d’activité se classent en glissant les lignes',
        texte:
          'Sur l’écran Types d’activité et tarifs, une poignée au début de chaque ligne permet de la glisser plus haut ou plus bas. Les activités suivent cet ordre partout — écran Activités, choix d’activité à l’inscription —, puis l’ordre alphabétique à l’intérieur d’un type. Le champ « Ordre d’affichage » de la fiche d’une activité disparaît.',
        permission: 'type_activite.lire',
      },
      {
        ecran: 'Inscriptions',
        titre: 'Les réductions se cochent, par type',
        texte:
          'Dans la saisie et le détail d’une inscription, le champ « Réduction » laisse place à une liste de cases, une par type de réduction, qui se cumulent. Une remise libre demande son montant et son motif. Les réductions saisies avant cette version apparaissent en ambre comme « non détaillées » : cochez le bon type puis retirez-les, à l’occasion — enregistrer la fiche pour autre chose ne les touche pas.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Filtrer et lire les réductions',
        texte:
          'La liste gagne un filtre Réduction, par type ou « Non détaillée ». L’infobulle de la pastille « réduit » énumère les réductions de la ligne, dans la liste comme sur la fiche adhérent, et l’export Excel les porte dans deux colonnes.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Adhérents',
        titre: 'L’école fréquentée se choisit dans un référentiel',
        texte:
          'Le champ « École fréquentée » propose désormais la liste des établissements connus au fil de la frappe : les 81 graphies des fiches (« St Sauveur », « Saint Sauveur », « Saint-Sauveur ») ont été ramenées à une cinquantaine d’établissements vérifiés, avec leur type, leur statut et leur arrondissement. Une école saisie qui n’y est pas entre dans le référentiel « à valider ».',
        permission: ADHERENTS,
      },
      {
        ecran: 'Référentiels',
        titre: 'Établissements scolaires',
        texte:
          'Un nouvel écran liste les établissements, avec une case pour ne voir que ceux à valider. On y complète les types, le statut et l’arrondissement, on valide, on renomme — le texte des fiches suit.',
        permission: 'etablissement.gerer',
      },
      {
        ecran: 'Paiements',
        titre: 'Le rapprochement se filtre, et s’écrit',
        texte:
          'L’onglet Rapprochement gagne quatre filtres à choix multiples — activité, jour, professeur, type de membre — dont les trois premiers se déduisent les uns des autres : choisir un professeur ne propose plus que ses activités et ses jours. Les activités de l’année s’affichent à côté de chaque nom, la colonne « Écart » s’appelle « Reste dû », et le total dû des lignes affichées remplace les trois tuiles. L’icône Gmail écrit aux adhérents des lignes affichées, par exemple ceux d’un cours qui n’ont pas soldé.',
        permission: 'rapprochement.lire',
      },
      {
        ecran: 'Adhérents',
        titre: 'Le droit à l’image, sur la fiche',
        texte:
          'La fiche d’un adhérent enregistre, pour l’année affichée, l’autorisation de droit à l’image signée par la famille, support par support : usage interne, site internet, réseaux sociaux, presse et imprimés — chacun non renseigné, accordé ou refusé —, avec la date de signature et un commentaire. Une pastille le rappelle sur la fiche, un point rouge dans la liste signale un refus ; l’écran Anomalies liste ceux dont l’autorisation n’est pas rentrée.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Anomalies',
        titre: 'Droit à l’image non renseigné',
        texte:
          'Un nouveau type d’anomalie liste les adhérents inscrits cette année dont l’autorisation n’est pas enregistrée. Il s’assume pour l’année comme les autres.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Des blocs repliés dans la fiche',
        texte:
          'Dans « Modifier la fiche », les blocs Droit à l’image et Handicap sont repliés : leur ligne de titre résume ce qui est saisi, un clic les ouvre.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Administration',
        titre: 'La convention collective sur la fiche entité',
        texte:
          'Le bloc Identité de la fiche d’une entité porte la convention collective de l’employeur et son code IDCC, à côté du numéro GUSO.',
        permission: ENTITES,
      },
      {
        ecran: 'Années scolaires',
        titre: 'La reprise d’année recopie les types de réduction',
        texte:
          'À la création d’une année, une troisième case reprend les types de réduction de l’année précédente, avec leur montant.',
        permission: 'annee.creer',
      },
    ],
  },
  {
    version: '0.65.0',
    date: '2026-10-09',
    changements: [
      {
        ecran: 'Adhérents',
        titre: 'Écrire à la sélection',
        texte:
          'L’icône Gmail, à côté de l’icône Excel de l’export, ouvre une communication groupée pour les adhérents affichés et leurs contacts — par exemple ceux du lundi avec un professeur. Chaque adresse n’apparaît qu’une fois, avec les personnes qu’elle concerne, et se décoche pour affiner ; ceux qui ne recevront rien sont nommés. « Ouvrir dans Gmail » prépare un brouillon depuis votre boîte, toutes les adresses en copie cachée.',
        permission: MESSAGES,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Écrire à la sélection',
        texte:
          'La même communication groupée depuis l’écran Inscriptions, par son icône Gmail : elle s’adresse aux élèves des inscriptions affichées et à leurs contacts — par exemple les inscriptions actives d’un professeur.',
        permission: MESSAGES,
      },
      {
        ecran: 'Planning',
        titre: 'Écrire aux élèves d’un créneau',
        texte:
          'Le menu ⋮ de la fiche d’un créneau propose « Écrire aux élèves » : la même communication groupée, pour les élèves inscrits à ce cours et leurs contacts.',
        permission: MESSAGES,
      },
      {
        ecran: 'Listes',
        titre: 'Export et envoi en icônes',
        texte:
          'Sur les écrans Adhérents, Inscriptions, Paiements, Inscriptions en ligne et Paie, l’export devient une icône Excel ; l’icône PDF des bordereaux de remise passe en couleur. Le libellé reste en infobulle.',
      },
      {
        ecran: 'Inscriptions',
        titre: 'Fin anticipée : forfait d’essai, prorata ou montant inchangé',
        texte:
          'La fin anticipée propose trois bases pour le montant dû : le prorata des séances suivies, le forfait d’essai — seule proposée quand l’essai n’est pas confirmé, la date se cale alors d’office sur la fin de l’essai et rien n’est proratisé —, ou le montant inchangé. Le montant reste modifiable.',
        permission: 'inscription.cloturer',
      },
      {
        ecran: 'Paiements',
        titre: 'Rembourser un trop-perçu par virement',
        texte:
          'Quand une inscription a reçu plus que son dû — après une fin anticipée, par exemple —, la Direction enregistre le remboursement : un virement de l’école vers la famille, qui apparaît comme un règlement négatif, « prévu » jusqu’à la date du virement. La fin anticipée le propose directement quand l’atelier était déjà payé ; la fiche adhérent a un bouton « Rembourser » pour les autres cas.',
        permission: 'reglement.rembourser',
      },
      {
        ecran: 'Inscriptions en ligne',
        titre: 'L’adresse de la fiche est bien comparée',
        texte:
          'En acceptant une demande rattachée à une fiche, l’adresse de la fiche ne s’affichait jamais et toute adresse saisie était proposée en ajout, même identique. La ligne Adresse montre maintenant les deux côtés, dit « identique » quand c’est le cas, et reste visible quand la famille n’a rien saisi.',
        permission: DEMANDES,
      },
      {
        ecran: 'Adhérents',
        titre: 'Filtres Activité, Jour et Professeur plus justes',
        texte:
          'Combinés, les filtres Activité, Jour et Professeur désignent désormais le même cours : « piano », « lundi » et « Aristide » donnent les élèves qui ont piano le lundi avec Aristide, et non plus aussi ceux qui viennent le lundi pour un autre cours. L’export Excel tient compte du filtre Activité, qu’il ignorait.',
        permission: ADHERENTS,
      },
    ],
  },
  {
    version: '0.64.0',
    date: '2026-10-08',
    changements: [
      {
        ecran: 'Adhérents',
        titre: 'Liste plus compacte, export plus riche',
        texte:
          'La colonne Adresse quitte la liste pour lui rendre de la place ; la fiche et l’export Excel la gardent. L’export ajoute à droite les contacts de chaque fiche : nom, prénom et email par qualité — mère, père, tuteur, conjoint, autre.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Un filtre Professeur',
        texte:
          'Entre Jour et Année scolaire, un filtre Professeur retient les adhérents qu’un membre du personnel encadre sur la période choisie. Les cinq filtres ont désormais la même largeur.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Un règlement partagé dit ce qu’il alloue aux autres',
        texte:
          'Sur la fiche, dans le bloc Paiements, un chèque qui couvre plusieurs adhérents indique en gris la part allouée à chacun des autres — « 120,00 € alloué à BELLIER Léandre ».',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Démarrage en cours d’année : enregistré, il ne se retire plus',
        texte:
          'Comme le forfait d’essai, la case « Démarrage en cours d’année » ne se décoche plus une fois l’inscription enregistrée ; la date de la première séance reste modifiable. Et quand le montant n’est pas le montant proratisé, le panneau le dit en rouge.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Le forfait d’essai a un montant, une fin et une confirmation',
        texte:
          'Le type d’activité porte le montant du forfait. Tant que l’essai n’est pas confirmé, l’élève ne doit que ce montant : le reste à payer de la fiche et l’écart de la liste des inscriptions le lisent. Un forfait enregistré ne se décoche plus ; quand l’élève poursuit, « Confirmer l’essai » — sur la fiche, dans la liste ou dans le détail — applique le plein tarif. Un essai dont la fin est passée sans confirmation s’affiche en rouge, et la tuile Forfait d’essai compte ceux qui attendent. Arrêter un essai non confirmé par une fin anticipée le résout aussi : l’élève doit alors le montant du forfait.',
        permission: INSCRIPTIONS,
      },
    ],
  },
  {
    version: '0.63.0',
    date: '2026-10-07',
    changements: [
      {
        ecran: 'Personnel',
        titre: 'Les blocs de la fiche se replient',
        texte:
          'Cliquer l’en-tête d’un bloc de la fiche le replie ou le rouvre, et le choix est retenu sur votre poste. Dans le bloc Documents, chaque type de document et chaque sous-dossier se replient aussi.',
        permission: 'personnel.lire',
      },
      {
        ecran: 'Inscriptions en ligne',
        titre: 'Rattacher une demande : vous décidez champ par champ',
        texte:
          'La demande se lit en un tableau : ce qui a été saisi en ligne, et, quand elle se rattache à une fiche existante, la valeur de la fiche et votre décision champ par champ — garder ou remplacer ; pour l’adresse, ignorer, ajouter en secondaire ou remplacer la primaire. L’adhésion suit le formulaire, la contribution à la vie de la chorale va dans le commentaire de la fiche, et une phrase résume ce qui va se passer. Un adhérent connu ou probable est pré-sélectionné, et la couleur dit ce qui sera gardé : en vert la valeur retenue, en gris l’écartée.',
        permission: DEMANDES,
      },
      {
        ecran: 'Inscriptions en ligne',
        titre: 'Le sous-menu « Demandes » s’appelle « Inscriptions en ligne »',
        texte: 'Même écran, même place dans le menu Adhérents : le nom dit ce qu’on y trouve, les demandes déposées par le formulaire public.',
        permission: DEMANDES,
      },
      {
        ecran: 'Inscriptions',
        titre: 'La colonne « Restant dû » s’appelle « Écart »',
        texte:
          'Même calcul et mêmes couleurs : rouge quand il reste à payer, vert quand on a trop reçu. Le nouveau nom dit les deux sens.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Paiements',
        titre: 'Deux virements du même montant le même jour ne sont plus un « doublon »',
        texte:
          'Le signalement de règlement identique ne joue plus que pour le même bénéficiaire. Trois adultes de la chorale qui virent 350 € le même jour ne se bloquent plus les uns les autres.',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Paie',
        titre: 'Un élément de paie se saisit aussi depuis l’onglet Cachet',
        texte:
          'Pour un salarié qui a des ateliers au cachet, « Nouvel élément » est disponible dans sa fiche de paie côté Cachet, comme au régime général — pour garder la trace d’un remplacement, par exemple. L’élément reste au cachet. La mention du recalcul par le CEA disparaît de ce côté.',
        permission: 'contrat.gerer',
      },
      {
        ecran: 'Adhérents',
        titre: 'Les règlements encaissés ont leur bandeau sur la fiche',
        texte:
          'Dans le bloc Paiements, un bandeau vert « Encaissés » sépare désormais les règlements encaissés des échéances à venir, avec leur nombre et leur total.',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Paiements',
        titre: 'Le détail d’un règlement encaissé se consulte',
        texte:
          'Un virement ou un chèque déjà encaissé, ou en remise déposée, s’ouvre désormais d’un clic — ventilation comprise — en lecture seule, avec la raison pour laquelle il ne se modifie pas. Dans l’onglet Règlements comme sur la fiche de l’adhérent.',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Le prorata d’un démarrage en cours d’année compte la réduction',
        texte:
          'Le prorata se calcule désormais sur le tarif moins la réduction, au centime, comme la fin anticipée — plus d’arrondi à l’euro. Un élève qui arrête un cours puis en commence un autre paie exactement le tarif net de l’année, réduction comprise une seule fois.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Paramètres',
        titre: 'Un compte Google à reconnecter se signale',
        texte:
          'Quand le jeton du compte technique ne peut plus être lu par le serveur, le bloc Compte technique Google le dit et propose de reconnecter, au lieu d’une erreur interne sur la fiche du salarié ou à l’archivage.',
        permission: 'parametre_systeme.gerer',
      },
    ],
  },
  {
    version: '0.62.0',
    date: '2026-10-04',
    changements: [
      {
        ecran: 'Personnel',
        titre: 'Les créneaux des deux écoles sur la fiche',
        texte:
          'La fiche d’un salarié montre ses créneaux dans toutes les écoles où vous êtes habilité, chacun avec le point de couleur de son école. Pour un administratif, le bloc Créneaux n’apparaît plus.',
        permission: 'personnel.lire',
      },
      {
        ecran: 'Personnel',
        titre: 'Les documents du Drive sur la fiche',
        texte:
          'Un bloc « Documents » montre, pour chaque école où la personne a un contrat, ce que son dossier du Drive contient — les fiches de paie déposées dans « RH / NOM Prénom / Fiche de paie ». Le dossier et chaque fichier s’ouvrent dans le Drive.',
        permission: 'contrat.lire',
      },
      {
        ecran: 'Personnel',
        titre: 'Les éléments de paie sur la fiche',
        texte:
          'Un bloc « Éléments de paie » suit les créneaux : le tableau de l’écran Paie pour cette personne, dans l’école active, mois par mois — le mois se choisit dans le bandeau du bloc. En consultation : la saisie reste dans l’écran Paie.',
        permission: 'contrat.lire',
      },
      {
        ecran: 'Personnel',
        titre: 'Le bloc Contrats de la fiche, allégé et cliquable',
        texte:
          'Chaque contrat porte le point de couleur de son école, et mutuelle et transport sont les icônes de l’écran Contrats. Cliquer un contrat de l’école active ouvre sa fiche, comme depuis l’écran Contrats, et le bouton « Ajouter » du bloc en crée un dans l’école active sans repasser par l’écran Contrats.',
        permission: 'contrat.lire',
      },
    ],
  },
  {
    version: '0.61.0',
    date: '2026-10-03',
    changements: [
      {
        ecran: 'Remises',
        titre: 'Le bordereau est un PDF',
        texte:
          'L’icône PDF de chaque ligne de la liste des remises, comme le bouton « Bordereau (PDF) » de la fiche, télécharge le document, produit par le serveur : paginé, en-tête de colonnes sur chaque page, total une seule fois. Avant le dépôt, le PDF porte un filigrane « Projet — non déposé ».',
        permission: REMISES,
      },
      {
        ecran: 'Remises',
        titre: 'Le bordereau est conservé au dépôt',
        texte:
          'Au dépôt d’une remise, le bordereau est produit et conservé par l’application, une seule fois. La fiche de la remise le montre, avec la date et l’auteur ; une remise déposée avant cette version propose « Produire et conserver ».',
        permission: REMISES,
      },
      {
        ecran: 'Remises',
        titre: 'Le bordereau est archivé sur le Drive de l’école',
        texte:
          'Au dépôt, le bordereau est produit une fois et part dans le Drive partagé de l’école, dans le dossier réglé pour les remises. Il se consulte ensuite depuis le Drive — la fiche et l’icône de la ligne ouvrent le fichier ; avant le dépôt, le bouton « Bordereau (PDF) » donne le document de contrôle.',
        permission: REMISES,
      },
      {
        ecran: 'Entités',
        titre: 'Le Drive de l’école et le compte technique',
        texte:
          'La fiche de l’école porte l’identifiant de son Drive partagé, un bouton pour tester la connexion, et la liste des types de documents : pour chacun, le dossier où l’application range ses fichiers, avec des variables comme {annee}. Le compte Google au nom duquel l’application dépose se connecte une fois, dans Administration → Paramètres.',
        permission: ENTITES,
      },
    ],
  },
  {
    version: '0.60.0',
    date: '2026-10-03',
    changements: [
      {
        ecran: 'Contrats',
        titre: 'Temps plein ou temps partiel',
        texte:
          'Chaque contrat indique son temps de travail. Un professeur ou un animateur à temps plein est payé au salaire fixe ; à temps partiel, il reste payé sur ses cours, comme avant.',
        permission: CONTRATS,
      },
      {
        ecran: 'Contrats',
        titre: 'Salaire mensuel fixe, par période',
        texte:
          'Pour un administratif ou un temps plein, le salaire se saisit dans la fiche du contrat : un montant net, une date de début et, quand il change, une nouvelle période. Un administratif à temps partiel indique aussi ses heures par semaine.',
        permission: CONTRATS,
      },
      {
        ecran: 'Contrats',
        titre: 'Un CDD a toujours une date de fin',
        texte:
          'Elle est obligatoire et son motif est « Fin de CDD ». Pour tout contrat, la fin se renseigne dans la fiche, bloc « Fin de contrat » : le lien « Clore » a disparu.',
        permission: CONTRATS,
      },
      {
        ecran: 'Contrats',
        titre: 'Liste des contrats',
        texte:
          'Cliquez une ligne pour ouvrir la fiche, réorganisée en cinq blocs. Seuls les contrats actifs sont affichés par défaut ; les icônes à côté du nom signalent la mutuelle et le titre de transport.',
        permission: CONTRATS,
      },
      {
        ecran: 'Paie',
        titre: 'La synthèse mensuelle s’ouvre depuis un mois',
        texte:
          'Cliquez l’en-tête d’un mois du tableau annuel pour voir sa synthèse. La clôture de la période et l’export Excel sont possibles depuis les deux vues.',
        permission: CONTRATS,
      },
      {
        ecran: 'Paie',
        titre: 'Onglet Cachet',
        texte:
          'Les cours au cachet se lisent mois par mois, comme au régime général. Les tableaux de paie sont triés par prénom.',
        permission: CONTRATS,
      },
      {
        ecran: 'Paie',
        titre: 'Durée d’un salaire fixe',
        texte:
          'Dans le détail des éléments de paie, un salaire fixe affiche sa durée mensuelle, calculée depuis les heures hebdomadaires de travail.',
        permission: CONTRATS,
      },
      {
        ecran: 'Entités',
        titre: 'Une variable de paie peut changer à une date déjà clôturée',
        texte:
          'Une nouvelle période de cotisation mutuelle ou de valeur du point peut démarrer dans un mois de paie déjà clôturé ; les nets de ces mois suivent la nouvelle valeur.',
        permission: CONTRATS,
      },
      {
        ecran: 'Activités',
        titre: 'Nombre de créneaux',
        texte:
          'Une colonne indique combien de créneaux ouverts chaque activité porte. Le globe à côté du libellé signale une activité proposée à l’inscription en ligne.',
        permission: ACTIVITES,
      },
      {
        ecran: 'Année scolaire',
        titre: 'Année clôturée, vraiment fermée',
        texte:
          'On ne peut plus déposer ni modifier une remise, ni poser ou retirer une adhésion sur une année clôturée.',
        permission: [PAIEMENTS, ADHERENTS],
      },
      {
        ecran: 'Inscriptions',
        titre: 'Forfait d’essai visible',
        texte:
          'La case « Forfait d’essai » reste visible sur une inscription qui le porte, même si le type d’activité ne le propose plus : l’enregistrer ne l’efface plus.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Général',
        titre: 'Fenêtres de saisie',
        texte:
          'Les fenêtres tiennent dans l’écran : leur titre reste visible et seul leur contenu défile.',
      },
    ],
  },
  {
    version: '0.59.0',
    date: '2026-09-30',
    changements: [
      {
        ecran: 'Entités',
        titre: 'La fiche de l’école',
        texte:
          'L’écran Entités devient une fiche complète : identité, adresse, inscription en ligne et variables de paie. Le code de l’école se modifie.',
        permission: ENTITES,
      },
      {
        ecran: 'Entités',
        titre: 'Variables de paie par période',
        texte:
          'La cotisation mutuelle et la valeur du point d’ancienneté se règlent par école et par période, sur la fiche de l’entité. La paie prend la valeur en vigueur chaque mois.',
        permission: CONTRATS,
      },
      {
        ecran: 'Contrats',
        titre: 'Qualifier la fin d’un contrat',
        texte:
          'La date de fin s’appelle « Fin effective » et porte un motif : fin de CDD, démission ou rupture conventionnelle. La paie s’arrête à cette date, transport compris.',
        permission: CONTRATS,
      },
      {
        ecran: 'Types d’activité',
        titre: 'Individuel ou groupe',
        texte:
          'Un type d’activité déclare si ses cours sont individuels ou de groupe. Le planning et la paie s’en servent pour nommer l’élève et signaler un prorata.',
        permission: ACTIVITES,
      },
      {
        ecran: 'Adhérents',
        titre: 'Téléphones et emails contrôlés',
        texte:
          'Un numéro se saisit avec ou sans espaces, avec son pays, et s’affiche au format local. Les adresses email sont vérifiées à la saisie.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Tarif proratisé en modification',
        texte:
          'Le tarif proratisé d’un démarrage en cours d’année s’enregistre aussi sur une inscription existante, et le montant revient au tarif quand on décoche la case.',
        permission: INSCRIPTIONS,
      },
    ],
  },
  {
    version: '0.58.0',
    date: '2026-09-29',
    changements: [
      {
        ecran: 'Adhérents',
        titre: 'Les tuiles de l’écran',
        texte:
          'La tuile Adhérents compte toute la cohorte, « Forfait d’essai » filtre la liste et « Répartition des âges » montre enfants et adultes. Un tag signale les adhérents en forfait d’essai, dans la liste comme sur le planning.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Contacts partagés dans la fratrie',
        texte:
          'Les contacts d’un frère ou d’une sœur apparaissent sur chaque fiche de la famille. Un contact partagé se modifie depuis la fiche qui le porte.',
        permission: ADHERENTS,
      },
      {
        ecran: 'Général',
        titre: 'Export en Excel',
        texte:
          'Tous les exports produisent un classeur Excel, avec des montants en nombres. Le fichier CSV a disparu.',
      },
      {
        ecran: 'Général',
        titre: 'Un champ facultatif se vide',
        texte:
          'Effacer un téléphone, un commentaire ou tout champ non obligatoire le retire bien de la fiche à l’enregistrement.',
      },
      {
        ecran: 'Planning',
        titre: 'Fermer un créneau',
        texte:
          'Un créneau se ferme depuis le menu de sa fiche au lieu d’être supprimé. Fermé, il quitte le planning et la saisie d’inscription ; un créneau avec des inscrits ne se ferme pas.',
        permission: PLANNING,
      },
      {
        ecran: 'Planning',
        titre: 'Le groupe Ateliers devient Planning',
        texte:
          'La grille s’ouvre d’un clic sur « Planning » ; la liste des créneaux est son sous-menu. Les filtres de la liste se répondent, et la grille n’affiche plus de compteurs : la couleur des cartes dit tout.',
        permission: PLANNING,
      },
    ],
  },
  {
    version: '0.57.1',
    date: '2026-09-25',
    changements: [
      {
        ecran: 'Inscription en ligne',
        titre: 'Une adresse à communiquer',
        texte:
          'Le formulaire public est joignable à inscription.artistic-online.com, sans accès au reste de l’application.',
        permission: DEMANDES,
      },
    ],
  },
  {
    version: '0.57.0',
    date: '2026-09-24',
    changements: [
      {
        ecran: 'Inscription en ligne',
        titre: 'Les chorales et l’orchestre s’inscrivent en ligne',
        texte:
          'Un adulte peut demander son inscription depuis une page publique, pendant la fenêtre d’ouverture réglée sur la fiche de l’école. La demande n’est pas une inscription : elle attend le secrétariat.',
        permission: DEMANDES,
      },
      {
        ecran: 'Demandes',
        titre: 'L’écran Demandes',
        texte:
          'Dans le menu Adhérents, les demandes reçues se traitent une à une : accepter en créant la fiche ou en la rattachant à une fiche connue, refuser avec un motif. Le menu affiche le nombre de demandes en attente.',
        permission: DEMANDES,
      },
      {
        ecran: 'Activités',
        titre: 'Proposer une activité en ligne',
        texte:
          'Une case sur la fiche de l’activité la rend visible sur le formulaire public.',
        permission: ACTIVITES,
      },
    ],
  },
  {
    version: '0.56.2',
    date: '2026-09-08',
    changements: [
      {
        ecran: 'Paie',
        titre: 'Les mois ouverts se calculent, les mois clôturés sont enregistrés',
        texte:
          'La paie des cours se calcule en direct depuis les inscriptions tant que le mois est ouvert. Clôturer la période, depuis la synthèse mensuelle, enregistre le mois tel qu’il est déclaré ; il ne bouge plus ensuite.',
        permission: CONTRATS,
      },
      {
        ecran: 'Paie',
        titre: 'Créneaux écartés',
        texte:
          'Sous la synthèse, la liste des créneaux que la paie n’a pas pu calculer — professeur sans contrat, activité sans taux horaire — pour les corriger avant de clôturer.',
        permission: CONTRATS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Un mois clôturé se protège',
        texte:
          'Une modification d’inscription qui changerait la paie d’un mois déjà clôturé est refusée, et le mois est nommé.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Général',
        titre: 'Listes déroulantes des filtres',
        texte: 'Les listes déroulantes ne passent plus sous les titres de colonnes figés.',
      },
    ],
  },
  {
    version: '0.55.0',
    date: '2026-09-07',
    changements: [
      {
        ecran: 'Inscriptions',
        titre: 'Le montant est celui du tarif',
        texte:
          'À la création d’une inscription, le montant suit le tarif de l’activité et ne se saisit plus. Une remise passe par la réduction ; un démarrage en cours d’année remplace le montant par le tarif proratisé.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Deux cours de la même activité',
        texte:
          'Un élève peut suivre deux cours de piano par semaine : l’unicité porte désormais sur le créneau, pas sur l’activité.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Membre actif et anomalies',
        texte:
          'Cocher « Membre actif » et lever une anomalie fonctionnent de nouveau — ces deux gestes échouaient depuis le 2 septembre.',
        permission: ADHERENTS,
      },
    ],
  },
  {
    version: '0.54.0',
    date: '2026-09-04',
    changements: [
      {
        ecran: 'Inscriptions',
        titre: 'La fin anticipée montre son calcul',
        texte:
          'Le dernier cours se choisit dans un calendrier des séances, et l’écran affiche le décompte avant de valider. Le prorata vit dans sa propre colonne : la réduction consentie est conservée.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Un menu d’actions sur l’inscription',
        texte:
          'Le détail d’une inscription porte un menu avec Transférer et Fin anticipée, y compris depuis la fiche adhérent. Une inscription arrêtée est en consultation seule.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Démarrage en cours d’année',
        texte:
          'La case n’est plus cochée pour tout le monde : elle ne l’est que si le premier cours est postérieur à la première séance. La date n’est plus pré-remplie.',
        permission: INSCRIPTIONS,
      },
    ],
  },
  {
    version: '0.51.0',
    date: '2026-09-04',
    changements: [
      {
        ecran: 'Inscriptions',
        titre: 'Transférer une inscription',
        texte:
          'Un adhérent change de cours en cours d’année, même après avoir payé. Même activité : seul l’horaire change ; activité différente : arrêt au prorata puis reprise, et l’argent suit.',
        permission: INSCRIPTIONS,
      },
      {
        ecran: 'Inscriptions',
        titre: 'Le tableau des inscriptions',
        texte:
          'Le professeur figure sur la ligne du créneau, la réduction est une pastille, et une colonne « Restant dû » remplace le statut.',
        permission: INSCRIPTIONS,
      },
    ],
  },
  {
    version: '0.50.0',
    date: '2026-09-04',
    changements: [
      {
        ecran: 'Général',
        titre: 'Titres de colonnes figés',
        texte:
          'Les tableaux gardent leurs titres de colonnes visibles quand on descend dans la page.',
      },
      {
        ecran: 'Planning',
        titre: 'Un cours individuel nomme son élève',
        texte:
          'Sur la grille, un cours individuel plein affiche le prénom et le nom de l’élève à la place du compteur.',
        permission: PLANNING,
      },
    ],
  },
  {
    version: '0.49.0',
    date: '2026-09-03',
    changements: [
      {
        ecran: 'Remises',
        titre: 'Le bordereau s’imprime en entier',
        texte:
          'Le bordereau de remise s’imprime sur plusieurs pages, tous les chèques et le total compris. Il est plus compact.',
        permission: REMISES,
      },
    ],
  },
  {
    version: '0.48.0',
    date: '2026-09-03',
    changements: [
      {
        ecran: 'Remises',
        titre: 'Nouvelle référence des remises',
        texte:
          'Une remise s’appelle R202609.1.artistic.2026-27 : le mois du dépôt, un numéro d’ordre, l’école et l’année scolaire, choisie à la création.',
        permission: REMISES,
      },
    ],
  },
  {
    version: '0.47.0',
    date: '2026-09-03',
    changements: [
      {
        ecran: 'Utilisateurs',
        titre: 'Sessions expirées',
        texte:
          'Les sessions expirées sont purgées chaque jour, un mois après leur fin. La liste des sessions ouvertes reste lisible.',
        permission: UTILISATEURS,
      },
    ],
  },
  {
    version: '0.46.0',
    date: '2026-09-02',
    changements: [
      {
        ecran: 'Planning',
        titre: 'Le planning se lit à la couleur',
        texte:
          'Rouge quand deux créneaux se chevauchent, jaune au-delà de la capacité, vert s’il reste de la place, blanc quand c’est complet. Un tag « réservé » marque un cours individuel pris mais non réglé.',
        permission: PLANNING,
      },
      {
        ecran: 'Remises',
        titre: 'Création d’une remise',
        texte:
          'Les chèques de la période sont bien cochés à l’ouverture, et la période par défaut inclut le dernier jour du mois.',
        permission: REMISES,
      },
    ],
  },
  {
    version: '0.45.0',
    date: '2026-09-02',
    changements: [
      {
        ecran: 'Paiements',
        titre: 'L’affectation d’un règlement se propose',
        texte:
          'À la saisie du montant, les lignes se remplissent dans l’ordre jusqu’à épuiser l’enveloppe, et chaque ligne annonce son reste à payer. On peut ensuite redistribuer librement.',
        permission: PAIEMENTS,
      },
      {
        ecran: 'Adhérents',
        titre: '« Payé » compte ce qui est arrivé',
        texte:
          'Sur la fiche, « Payé » inclut la part reçue mais pas encore affectée. Le bloc Inscriptions et le bloc Paiements disent le même montant, et la fiche se rafraîchit après un paiement.',
        permission: ADHERENTS,
      },
    ],
  },
  {
    version: '0.44.1',
    date: '2026-09-01',
    changements: [
      {
        ecran: 'Utilisateurs',
        titre: 'Adresse d’origine des sessions',
        texte:
          'La liste des sessions ouvertes affiche la vraie adresse du poste, et non celle du relais interne.',
        permission: UTILISATEURS,
      },
    ],
  },
  {
    version: '0.44.0',
    date: '2026-09-01',
    changements: [
      {
        ecran: 'Utilisateurs',
        titre: 'Les sessions ouvertes se consultent',
        texte:
          'Administration → Utilisateurs montre qui a une session, depuis où, ouverte quand et expirant quand. Une session au-delà du délai d’inactivité est marquée dormante.',
        permission: UTILISATEURS,
      },
      {
        ecran: 'Adhérents',
        titre: 'Les filtres de l’écran',
        texte:
          'Quatre filtres à choix multiples — Activité, Jour, Année scolaire, Type — remplacent les anciens contrôles. L’écran s’ouvre sur les usagers et membres actifs de l’année, et chaque filtre actif s’affiche en pastille.',
        permission: ADHERENTS,
      },
    ],
  },
  {
    version: '0.42.1',
    date: '2026-09-01',
    changements: [
      {
        ecran: 'Paie',
        titre: 'Un élément de paie vaut un mois',
        texte:
          'Chaque ligne de paie porte son mois, son montant, sa prime et ses heures. L’écran Élément de paie se lit mois par mois, et un élément exceptionnel peut être récurrent jusqu’à une date de fin.',
        permission: CONTRATS,
      },
      {
        ecran: 'Paie',
        titre: 'Les éléments de paie viennent des créneaux',
        texte:
          'La paie des cours se déduit des créneaux et des inscriptions, avec un prévisionnel des mois à venir. Les lignes générées ne se modifient pas à la main : on ajoute un élément exceptionnel.',
        permission: CONTRATS,
      },
      {
        ecran: 'Général',
        titre: 'Session perdue',
        texte:
          'Une session expirée en cours de route renvoie à la page de connexion, au lieu d’afficher « Session expirée » dans chaque écran.',
      },
    ],
  },
];

/** La version de tete : celle que l'ecran annonce, lue ou non. */
export const VERSION_COURANTE = NOUVEAUTES[0]!.version;
